import { PropsWithChildren, useMemo } from "react";
import { Client, fetchExchange, Provider, subscriptionExchange } from "urql";
import { createClient as createWSClient } from "graphql-ws";
import { useLocale } from "@acto/web-common/locales";
import { cache } from "./cache";
import { queryRevalidationExchange } from "./requestPolicy";
import { schemaMismatchReloadExchange } from "./schemaMismatchReload";

interface ActoGraphqlProviderProps extends PropsWithChildren {
  accessToken: string | undefined;
  graphqlEndpoint: string;
  wsEndpoint?: string;
  /** Which app this client runs in, reported to the api for per-build usage attribution. */
  clientName?: "widget" | "management";
}

export const ActoGraphqlProvider = ({
  accessToken,
  graphqlEndpoint,
  wsEndpoint,
  clientName = "widget",
  children,
}: ActoGraphqlProviderProps) => {
  if (!graphqlEndpoint) {
    throw new Error("GraphQL endpoint is required");
  }

  const { locale } = useLocale();

  const wsClient = useMemo(() => {
    if (!wsEndpoint || !accessToken) return null;

    return createWSClient({
      url: wsEndpoint,
      connectionParams: () => ({
        authorization: `Bearer ${accessToken}`,
      }),
      retryAttempts: 5,
      shouldRetry: () => true,
      retryWait: async (retries) => {
        await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** retries, 30000)));
      },
    });
  }, [wsEndpoint, accessToken]);

  const client = useMemo(() => {
    const reloadOnSchemaMismatch = schemaMismatchReloadExchange(import.meta.env.VITE_BUILD_VERSION);
    const exchanges = wsClient
      ? [
          reloadOnSchemaMismatch,
          queryRevalidationExchange,
          cache,
          subscriptionExchange({
            forwardSubscription: (request) => ({
              subscribe: (sink) => ({
                unsubscribe: wsClient.subscribe({ ...request, query: request.query || "" }, sink),
              }),
            }),
          }),
          fetchExchange,
        ]
      : [reloadOnSchemaMismatch, queryRevalidationExchange, cache, fetchExchange];

    return new Client({
      url: graphqlEndpoint,
      exchanges,
      fetchOptions: () => {
        const headers: Record<string, string> = {
          "Accept-Language": locale,
          "X-Acto-Locale": locale,
          "X-Acto-Client": clientName,
          "X-Acto-Client-Build": import.meta.env.VITE_BUILD_VERSION ?? "local",
          ...(import.meta.env.VITE_MIRRORD_USER && {
            "x-acto-dev-user": import.meta.env.VITE_MIRRORD_USER,
          }),
        };

        if (accessToken) {
          headers["authorization"] = `Bearer ${accessToken}`;
        }

        return { headers };
      },
    });
  }, [accessToken, locale, graphqlEndpoint, wsClient, clientName]);

  return <Provider value={client}>{children}</Provider>;
};
